import React, { useState, useEffect } from 'react';

import { geofenceAreaConfigAPI } from '../../../utils/apiClient';
import { Button } from '../../common/Button';
import { AREA_SYMBOLS, AREA_SYMBOL_LABELS } from '../../icons/AreaSymbols';

interface AreaConfig {
  id: number;
  area_name: string;
  symbol_key: string | null;
}

interface Props {
  onClose: () => void;
  inline?: boolean;
}

const SYMBOL_KEYS = Object.keys(AREA_SYMBOL_LABELS);

export const GeofenceSymbolsModal: React.FC<Props> = ({ onClose, inline }) => {
  const [configs, setConfigs] = useState<AreaConfig[]>([]);
  const [availableAreas, setAvailableAreas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAreaName, setNewAreaName] = useState('');
  const [newSymbolKey, setNewSymbolKey] = useState(SYMBOL_KEYS[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingSymbolKey, setEditingSymbolKey] = useState<string>(SYMBOL_KEYS[0]);

  useEffect(() => {
    Promise.all([geofenceAreaConfigAPI.list(), geofenceAreaConfigAPI.listAreaNames()])
      .then(([configsRes, areasRes]) => {
        setConfigs(
          configsRes.configs.map((c) => ({
            id: c.id,
            area_name: c.area_name,
            symbol_key: c.symbol_key ?? null,
          }))
        );
        setAvailableAreas(areasRes.areas);
        if (areasRes.areas.length > 0) setNewAreaName(areasRes.areas[0]);
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!newAreaName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await geofenceAreaConfigAPI.upsert(newAreaName.trim(), newSymbolKey);
      setConfigs((prev) => {
        const existing = prev.findIndex((c) => c.area_name === res.area_name);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = res;
          return updated;
        }
        return [...prev, res].sort((a, b) => a.area_name.localeCompare(b.area_name));
      });
      setNewAreaName('');
    } catch (err: any) {
      setError('Failed to save' + (err?.message ? ': ' + err.message : ''));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (c: AreaConfig) => {
    setSaving(true);
    setError(null);
    try {
      const res = await geofenceAreaConfigAPI.upsert(c.area_name, editingSymbolKey);
      setConfigs((prev) =>
        prev.map((item) => (item.id === c.id ? { ...item, symbol_key: res.symbol_key } : item))
      );
      setEditingId(null);
    } catch (err: any) {
      setError('Failed to save' + (err?.message ? ': ' + err.message : ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    setError(null);
    try {
      await geofenceAreaConfigAPI.delete(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError('Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.8vmin',
    fontSize: '1.6vmin',
    border: '1px solid #ccc',
    borderRadius: '0.4vmin',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={
        inline
          ? {}
          : {
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: 'max(20px, 5vh)',
              zIndex: 3000,
            }
      }
      onClick={inline ? undefined : onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          padding: '2vmin',
          ...(inline
            ? {}
            : {
                maxWidth: '520px',
                width: '90%',
                maxHeight: 'min(90vh, 85dvh)',
                overflowY: 'scroll',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
              }),
          boxShadow: inline ? 'none' : '0 4px 8px rgba(0,0,0,0.2)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2vmin',
            borderBottom: '2px solid #ddd',
            paddingBottom: '1vmin',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '2.5vmin' }}>Geofence area symbols</h2>
          {!inline && (
            <Button
              text="✕"
              onClick={onClose}
              style={{
                background: '#f44',
                color: 'white',
                fontSize: '2vmin',
                padding: '0.5vmin 1vmin',
              }}
            />
          )}
        </div>

        <p style={{ fontSize: '1.6vmin', color: '#555', marginBottom: '2vmin' }}>
          Link a symbol to a geofence area name. The symbol will appear on device markers when a
          device is located in that area.
        </p>

        {/* Existing configs */}
        {loading ? (
          <div style={{ fontSize: '1.6vmin', color: '#888' }}>Loading...</div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '1.6vmin',
              marginBottom: '2vmin',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: '0.6vmin 0.8vmin' }}>Area name</th>
                <th style={{ padding: '0.6vmin 0.8vmin' }}>Symbol</th>
                <th style={{ padding: '0.6vmin 0.8vmin' }} />
              </tr>
            </thead>
            <tbody>
              {configs.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ padding: '1vmin', color: '#aaa', fontStyle: 'italic' }}>
                    No symbols configured yet
                  </td>
                </tr>
              )}
              {configs.map((c) => {
                const isEditing = editingId === c.id;
                const Symbol = c.symbol_key ? AREA_SYMBOLS[c.symbol_key] : null;
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.6vmin 0.8vmin' }}>{c.area_name}</td>
                    <td style={{ padding: '0.6vmin 0.8vmin' }}>
                      {isEditing ? (
                        <select
                          value={editingSymbolKey}
                          onChange={(e) => setEditingSymbolKey(e.target.value)}
                          style={{ ...inputStyle }}
                        >
                          {SYMBOL_KEYS.map((key) => (
                            <option key={key} value={key}>
                              {AREA_SYMBOL_LABELS[key]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8vmin' }}>
                          {Symbol && <Symbol size={20} />}
                          {c.symbol_key ? (AREA_SYMBOL_LABELS[c.symbol_key] ?? c.symbol_key) : '–'}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '0.6vmin 0.8vmin',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isEditing ? (
                        <div
                          style={{ display: 'flex', gap: '0.5vmin', justifyContent: 'flex-end' }}
                        >
                          <Button
                            text="Save"
                            onClick={() => handleEdit(c)}
                            disabled={saving}
                            style={{
                              background: '#4CAF50',
                              color: 'white',
                              fontSize: '1.4vmin',
                              padding: '0.4vmin 0.8vmin',
                            }}
                          />
                          <Button
                            text="Cancel"
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                            style={{
                              background: '#888',
                              color: 'white',
                              fontSize: '1.4vmin',
                              padding: '0.4vmin 0.8vmin',
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{ display: 'flex', gap: '0.5vmin', justifyContent: 'flex-end' }}
                        >
                          <Button
                            text="Edit"
                            onClick={() => {
                              setEditingId(c.id);
                              setEditingSymbolKey(c.symbol_key ?? SYMBOL_KEYS[0]);
                            }}
                            disabled={saving}
                            style={{
                              background: '#2196F3',
                              color: 'white',
                              fontSize: '1.4vmin',
                              padding: '0.4vmin 0.8vmin',
                            }}
                          />
                          <Button
                            text="Remove"
                            onClick={() => handleDelete(c.id)}
                            disabled={saving}
                            style={{
                              background: '#f44',
                              color: 'white',
                              fontSize: '1.4vmin',
                              padding: '0.4vmin 0.8vmin',
                            }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Add new */}
        <div
          style={{
            borderTop: '1px solid #ddd',
            paddingTop: '1.5vmin',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: '1vmin',
            alignItems: 'end',
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '1.4vmin',
                fontWeight: 'bold',
                marginBottom: '0.3vmin',
              }}
            >
              Area name
            </label>
            {availableAreas.length > 0 ? (
              <select
                value={newAreaName}
                onChange={(e) => setNewAreaName(e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
              >
                {availableAreas.map((area) => (
                  <option key={area} value={area}>
                    {area}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={newAreaName}
                onChange={(e) => setNewAreaName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder="e.g. Engine Room"
                style={{ ...inputStyle, width: '100%' }}
              />
            )}
          </div>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '1.4vmin',
                fontWeight: 'bold',
                marginBottom: '0.3vmin',
              }}
            >
              Symbol
            </label>
            <select
              value={newSymbolKey}
              onChange={(e) => setNewSymbolKey(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            >
              {SYMBOL_KEYS.map((key) => (
                <option key={key} value={key}>
                  {AREA_SYMBOL_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
          <Button
            text="Add"
            onClick={handleAdd}
            disabled={saving || !newAreaName.trim()}
            style={{
              background: '#4CAF50',
              color: 'white',
              fontSize: '1.6vmin',
              padding: '0.8vmin 1.2vmin',
            }}
          />
        </div>

        {error && (
          <div style={{ marginTop: '1vmin', fontSize: '1.5vmin', color: '#f44' }}>{error}</div>
        )}
      </div>
    </div>
  );
};
